import { useQuery } from "@tanstack/react-query";

export type PlanOfCareDetails = {
  organization: {
    name: string;
    phoneNumber: string;
    timezone: string;
    location: string;
    allowSameDayAppointmentsAcrossProviderGroups?: boolean;
  };
  agentActive: boolean;
  patient: {
    archived?: boolean;
  };
  agent: {
    cancelEnabled: boolean;
    chargeCancellationFee: boolean;
    cancellationFee?: {
      window?: "Always" | number;
      amount?: number;
    };
    noCancellationWindowHours?: number;
  };
  planOfCares: {
    gap: number;
    frequency?: number;
    discharged: boolean;
    selected: boolean;
    planOfCareId: string;
    caseStudyId: number | undefined;
    caseStudyName: string;
    authorization?: { startDate: string; endDate: string; visitLimit: number };
    authorizationsByType?: Record<string, { startDate: string; endDate: string; visitLimit: number | null }[]>;
    ignoreAuthorization?: boolean;
    appointments?: {
      id: number;
      start: string;
      end: string;
      apptStatus: string;
      aptType: string;
      calTitle: string;
      caseId: number;
      caseTitle: string;
    }[];
    periods: {
      periodStart: string;
      periodEnd: string;
      appointments: {
        status:
          | "occurred"
          | "missed"
          | "scheduled"
          | "unscheduled"
          | "unauthorized";
        id: string;
        appointmentTypeId: string;
        appointmentTypeName: string;
        appointmentDuration: number;
        start?: string;
      }[];
      extraAppointments: {
        status: "occurred" | "scheduled";
        id: number;
        appointmentTypeId: string;
        appointmentTypeName: string;
        appointmentDuration: number;
        start?: string;
      }[];
    }[];
  }[];
};

export type PlanOfCareParams = {
  uniqueId?: string;
  enabled: boolean;
};

export function usePlanOfCareDetails({
  uniqueId,
  enabled = true
}: PlanOfCareParams) {
  return useQuery({
    queryKey: ["plan-of-care", uniqueId],
    queryFn: async () => {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/bookings/plan-of-care?uniqueId=${uniqueId}`,
        {
          method: "GET"
        }
      );
      if (response.status !== 200) {
        if (
          response.status === 404 ||
          response.status === 400 ||
          response.status === 410
        ) {
          throw new Error((await response.json()).message);
        } else {
          throw new Error("Error loading plan of care details");
        }
      }
      return response.json();
    },
    retry: 2,
    staleTime: 1000 * 60 * 60, // cache for 1 hour
    enabled
  });
}
